import React from "react";
import i18n from "i18next";
import k from "src/i18n/keys";

import { formatHumanReadableDate } from "src/utils";
import Deleted from "src/components/messages/Chat/Deleted";
import Hidden from "src/components/messages/Chat/Hidden";
import Notification from "src/components/messages/Chat/Notification";
import Chat from "src/components/messages/Chat";

import type { UnifizeChat, RoomId } from "src/types";
import type { GroupedChatMetadata } from "./utils";
import type { Nullable } from "src/ts-types";

import { DateBreak } from "./styles";
import ViewAuditTrail from "./ViewAuditTrail";

interface Props {
  sticky: boolean;
  collapsibleChat: UnifizeChat & GroupedChatMetadata;
  notificationsExpanded: boolean;
  toggleExpansion: (e: Nullable<HTMLDivElement>) => void;
  lastMessage: Nullable<string>;
  renderMessageMarker: () => React.ReactNode;
  roomId: RoomId;
  firstMessage?: boolean;
  stackCount: number;
  dataIndex: number;
}

const CollapsibleChat = React.forwardRef(
  (
    {
      sticky,
      collapsibleChat,
      notificationsExpanded,
      toggleExpansion,
      lastMessage,
      renderMessageMarker,
      roomId,
      firstMessage: _firstMessage,
      stackCount = 0,
      dataIndex,
    }: Props,
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const {
      id,
      deleted,
      hidden,
      action,
      timestamp,
      dateBreak,
      collapsedState,
    } = collapsibleChat;
    const messageMarker = lastMessage === id ? renderMessageMarker() : null;

    const renderChatContent = () => {
      if (deleted) {
        return <Deleted message={collapsibleChat} first={true} />;
      }
      if (hidden) {
        return <Hidden message={collapsibleChat} />;
      }
      if (action) {
        return (
          <Notification
            message={collapsibleChat}
            roomId={roomId}
            type={"first"}
          />
        );
      }
      return <Chat message={collapsibleChat} type={"first"} />;
    };

    return collapsedState !== "stacked" || notificationsExpanded ? (
      <>
        {dateBreak && (
          <DateBreak belowViewAuditTrail={sticky}>
            {new Date(timestamp).toDateString() === new Date().toDateString()
              ? i18n.t(k.TODAY)
              : formatHumanReadableDate(timestamp)}
          </DateBreak>
        )}
        {collapsedState === "first" && (
          <ViewAuditTrail
            sticky={sticky}
            toggleExpansion={toggleExpansion}
            expanded={notificationsExpanded}
            count={stackCount}
          />
        )}
        {(collapsedState === "none" || notificationsExpanded) && (
          <div ref={ref} data-index={dataIndex}>
            <>
              {messageMarker}
              {renderChatContent()}
            </>
          </div>
        )}
      </>
    ) : null;
  },
);

export default CollapsibleChat;
